import { formatCountdown, normalizeRemainingSeconds } from "./format";

export const COUNTDOWN_THRESHOLDS = {
  armAtSeconds: 120,
  hundredDesignAtSeconds: 100,
  sixtyDesignAtSeconds: 60,
  fullscreenAtSeconds: 30,
  thirtyDesignAtSeconds: 30,
  fifteenDesignAtSeconds: 15,
  tenDesignAtSeconds: 10,
  individualDesignAtSeconds: 5,
} as const;

export type CountdownPhase =
  | "disabled"
  | "minutes"
  | "two-minute-seconds"
  | "hundred"
  | "sixty"
  | "thirty"
  | "fifteen"
  | "ten"
  | "five"
  | "four"
  | "three"
  | "two"
  | "one"
  | "zero";

export type CountdownDisplayMode = "carousel" | "pinned" | "fullscreen";

export type CountdownConfiguration =
  | {
      enabled: false;
      /** Retained by settings UIs, but deliberately ignored by the engine. */
      targetEpochMs?: number | null;
    }
  | {
      enabled: true;
      targetEpochMs: number;
    };

export interface CountdownState {
  readonly enabled: boolean;
  readonly targetEpochMs: number | null;
  readonly remainingSeconds: number | null;
  readonly lastEvaluatedAtEpochMs: number | null;
  /** Becomes true at <=120 seconds and remains armed until slide entry. */
  readonly pinArmed: boolean;
  /** Set only by an explicit countdown-slide entry after arming. */
  readonly pinned: boolean;
  /** Latched so a small backwards time correction cannot leave full screen. */
  readonly fullscreenLatched: boolean;
  /** Latched at zero: the countdown never starts counting upward again. */
  readonly completed: boolean;
}

export type CountdownAction =
  | {
      type: "configure";
      configuration: CountdownConfiguration;
      nowEpochMs: number;
    }
  | {
      type: "tick";
      nowEpochMs: number;
    }
  | {
      /** Dispatch on the edge where this slide becomes the active slide. */
      type: "slide-enter";
    };

export interface DisabledCountdownView {
  readonly enabled: false;
  readonly phase: "disabled";
  readonly displayMode: "carousel";
  readonly remainingSeconds: null;
  readonly formattedRemaining: null;
  readonly shouldAdvanceSlides: true;
  readonly isStopped: false;
}

export interface EnabledCountdownView {
  readonly enabled: true;
  readonly phase: Exclude<CountdownPhase, "disabled">;
  readonly displayMode: CountdownDisplayMode;
  readonly remainingSeconds: number;
  readonly formattedRemaining: string;
  readonly shouldAdvanceSlides: boolean;
  readonly isStopped: boolean;
}

export type CountdownView = DisabledCountdownView | EnabledCountdownView;

function assertEpochMs(value: number, name: string): void {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be a finite epoch-millisecond value`);
  }
}

export function secondsUntil(targetEpochMs: number, nowEpochMs: number): number {
  assertEpochMs(targetEpochMs, "targetEpochMs");
  assertEpochMs(nowEpochMs, "nowEpochMs");

  return normalizeRemainingSeconds((targetEpochMs - nowEpochMs) / 1_000);
}

export function classifyCountdownPhase(
  remainingSeconds: number,
): Exclude<CountdownPhase, "disabled"> {
  const remaining = normalizeRemainingSeconds(remainingSeconds);

  if (remaining === 0) return "zero";
  if (remaining === 1) return "one";
  if (remaining === 2) return "two";
  if (remaining === 3) return "three";
  if (remaining === 4) return "four";
  if (remaining === 5) return "five";
  if (remaining <= COUNTDOWN_THRESHOLDS.tenDesignAtSeconds) return "ten";
  if (remaining <= COUNTDOWN_THRESHOLDS.fifteenDesignAtSeconds) return "fifteen";
  if (remaining <= COUNTDOWN_THRESHOLDS.thirtyDesignAtSeconds) return "thirty";
  if (remaining <= COUNTDOWN_THRESHOLDS.sixtyDesignAtSeconds) return "sixty";
  if (remaining <= COUNTDOWN_THRESHOLDS.hundredDesignAtSeconds) return "hundred";
  if (remaining <= COUNTDOWN_THRESHOLDS.armAtSeconds) return "two-minute-seconds";

  return "minutes";
}

export function createCountdownState(
  configuration: CountdownConfiguration,
  nowEpochMs: number,
): CountdownState {
  assertEpochMs(nowEpochMs, "nowEpochMs");

  if (!configuration.enabled) {
    return {
      enabled: false,
      targetEpochMs: configuration.targetEpochMs ?? null,
      remainingSeconds: null,
      lastEvaluatedAtEpochMs: nowEpochMs,
      pinArmed: false,
      pinned: false,
      fullscreenLatched: false,
      completed: false,
    };
  }

  assertEpochMs(configuration.targetEpochMs, "targetEpochMs");
  const remainingSeconds = secondsUntil(configuration.targetEpochMs, nowEpochMs);
  const completed = remainingSeconds === 0;

  return {
    enabled: true,
    targetEpochMs: configuration.targetEpochMs,
    remainingSeconds,
    lastEvaluatedAtEpochMs: nowEpochMs,
    pinArmed:
      !completed &&
      remainingSeconds > COUNTDOWN_THRESHOLDS.fullscreenAtSeconds &&
      remainingSeconds <= COUNTDOWN_THRESHOLDS.armAtSeconds,
    pinned: false,
    fullscreenLatched:
      remainingSeconds <= COUNTDOWN_THRESHOLDS.fullscreenAtSeconds,
    completed,
  };
}

export function transitionCountdown(
  state: CountdownState,
  action: CountdownAction,
): CountdownState {
  if (action.type === "configure") {
    return createCountdownState(action.configuration, action.nowEpochMs);
  }

  if (action.type === "slide-enter") {
    const canPin =
      state.enabled &&
      !state.completed &&
      state.pinArmed &&
      state.remainingSeconds !== null &&
      state.remainingSeconds > COUNTDOWN_THRESHOLDS.fullscreenAtSeconds;

    if (!canPin) return state;

    return {
      ...state,
      pinArmed: false,
      pinned: true,
    };
  }

  assertEpochMs(action.nowEpochMs, "nowEpochMs");

  if (!state.enabled || state.targetEpochMs === null) {
    return {
      ...state,
      remainingSeconds: null,
      lastEvaluatedAtEpochMs: action.nowEpochMs,
      pinArmed: false,
      pinned: false,
      fullscreenLatched: false,
      completed: false,
    };
  }

  // Once zero was rendered, keep it there even if a later clock correction or
  // device-clock edit would otherwise make the value positive again.
  if (state.completed) {
    return {
      ...state,
      remainingSeconds: 0,
      lastEvaluatedAtEpochMs: action.nowEpochMs,
    };
  }

  const calculatedRemaining = secondsUntil(
    state.targetEpochMs,
    action.nowEpochMs,
  );
  const completed = calculatedRemaining === 0;
  const fullscreenLatched =
    state.fullscreenLatched ||
    calculatedRemaining <= COUNTDOWN_THRESHOLDS.fullscreenAtSeconds;

  return {
    ...state,
    remainingSeconds: completed ? 0 : calculatedRemaining,
    lastEvaluatedAtEpochMs: action.nowEpochMs,
    pinArmed:
      !state.pinned &&
      !completed &&
      !fullscreenLatched &&
      (state.pinArmed ||
        calculatedRemaining <= COUNTDOWN_THRESHOLDS.armAtSeconds),
    fullscreenLatched,
    completed,
  };
}

export function getCountdownView(state: CountdownState): CountdownView {
  if (!state.enabled || state.remainingSeconds === null) {
    return {
      enabled: false,
      phase: "disabled",
      displayMode: "carousel",
      remainingSeconds: null,
      formattedRemaining: null,
      shouldAdvanceSlides: true,
      isStopped: false,
    };
  }

  const displayMode: CountdownDisplayMode = state.fullscreenLatched
    ? "fullscreen"
    : state.pinned
      ? "pinned"
      : "carousel";

  return {
    enabled: true,
    phase: classifyCountdownPhase(state.remainingSeconds),
    displayMode,
    remainingSeconds: state.remainingSeconds,
    formattedRemaining: formatCountdown(state.remainingSeconds),
    shouldAdvanceSlides: displayMode === "carousel",
    isStopped: state.completed,
  };
}
